import React from 'react'
import injectSheet from 'react-jss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCrown } from '@fortawesome/pro-solid-svg-icons/faCrown'
import ButtonElement from 'pobble-components/dist/button-element'
import { useState } from 'react'
import { useEffect } from 'react'
import { apiClient } from 'pobble-components/dist/api'
import { Image } from 'pobble-components/dist/image'
import { useHistory } from 'react-router-dom'
import { errorHandler } from 'pobble-components/dist/error-handler'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import { UpsellModal } from '../upsell'
import duckRocket from '../duck-rocket.gif'
import { useMemo } from 'react'
import { PromptsLayout } from '../../../prompts-layout'

const TopicsUpsell = injectSheet({
  img: {
    width: '175px !important',
    height: '175px !important',
    margin: '0 auto 24px !important'
  }
})(({ classes, ...props }) => {
  return (
    <UpsellModal
      id="quickwrites"
      ctaClassName="metrics__subscription_modal--edit"
      {...props}>
      <h2>Unlock unlimited writing inspiration!</h2>
      <img
        className={classes.img}
        src={duckRocket}
        width="175"
        height="175"
        alt="Duck in a rocket"
      />
      <p>
        Your free plan only allows access to our 'Chocolate' themed lesson. With
        a <strong>Teaching and Learning</strong> subscription, you unlock
        unlimited access to all of Pobble’s inspiring content, including all
        topic lessons!
      </p>
    </UpsellModal>
  )
})

export const Weekly = injectSheet({
  container: {
    background: '#191919',
    color: '#fff',
    minHeight: '100vh',
    padding: '0 0 120px'
  },
  main: {
    padding: '0 24px',
    overflow: 'hidden'
  },
  presentations: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 0 24px'
  },
  presentationsUpcoming: {
    // Hide 3rd button on portrait tablets
    '@media (min-width: 767px) and (max-width: 1023px)': {
      '& > :nth-child(3)': {
        display: 'none'
      }
    }
  },
  highlight: {
    maxWidth: '560px',
    margin: '0 auto',
    padding: '0 0 48px',
    '& $presentation': {
      width: '100%',
      display: 'block',
      outline: '2px solid #fff',
      outlineOffset: '4px',
      position: 'relative'
    },
    '& $presentationTitle': {
      '@media (min-width: 1024px)': {
        font: 'var(--heading)'
      }
    }
  },
  presentation: {
    paddingBottom: '56.2%',
    background: '#242424',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
    '& *': {
      pointerEvents: 'none'
    },
    '&:focus, &:active': {
      outline: '2px solid #fff',
      outlineOffset: '2px'
    }
  },
  presentationHolder: {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '100%',
    height: '100%',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    }
  },
  presentationTitle: {
    font: 'var(--heading--)',
    background: 'linear-gradient(to top, rgba(0, 0, 0, .8), rgba(0, 0, 0, 0))',
    color: '#fff',
    textAlign: 'center',
    padding: '120px 12px 12px',
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '100%',
    opacity: '0',
    animation: 'appear .3s forwards',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  spinner: {
    fontSize: '20px',
    color: 'var(--yellow)',
    margin: '0 0 8px'
  },
  free: {
    '&:after': {
      content: '"Free"',
      position: 'absolute',
      right: '8px',
      top: '8px',
      color: 'var(--gray)',
      background: '#fff',
      font: 'var(--body-)',
      fontWeight: 'bold',
      padding: '4px 16px',
      borderRadius: '4px'
    }
  },
  crown: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    width: '24px',
    height: '24px',
    borderRadius: '24px',
    fontSize: '10px',
    background: 'rgba(0, 0, 0, .3)',
    color: 'var(--yellow)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  featuredTitle: {
    textAlign: 'center',
    margin: '48px 0 24px',
    font: 'var(--heading-)',
    color: 'var(--yellow)',
    '@media (min-width: 768px)': {
      font: 'var(--heading_)'
    }
  },
  upcomingTitle: {
    textAlign: 'center',
    margin: '48px 0 24px',
    font: 'var(--heading-)',
    color: '#fff'
  },
  previousTitle: {
    textAlign: 'center',
    margin: '48px 0 24px',
    font: 'var(--heading-)',
    color: 'rgba(255, 255, 255, .7)'
  },
  '@keyframes appear': {
    to: {
      transform: 'translateY(0)',
      opacity: '1'
    }
  }
})(({ classes }) => {
  const [featured, setFeatured] = useState(null)
  const [presentations, setPresentations] = useState(null)
  const [upsell, setUpsell] = useState(false)

  const { hasAccessToTL } = useSubscriptionDetails()

  const history = useHistory()

  /**
   * Fetch presentations for selected year group
   */
  useEffect(() => {
    Promise.all([
      apiClient
        .get('v3/presentations/search', {
          params: {
            recommended: true,
            from: 0,
            limit: 1
          }
        })
        .catch(errorHandler),
      apiClient
        .get('v3/presentations/search', {
          params: {
            categories: ['quick_writes'],
            from: 1,
            limit: 200
          }
        })
        .catch(errorHandler)
    ]).then(([res1, res2]) => {
      setFeatured(res1?.data?.presentations?.[0] ?? null)
      setPresentations(res2?.data?.presentations ?? [])
    })
  }, [])

  const memoizedWeeklyJSX = useMemo(
    () => {
      return (
        <>
          <h2 className={classes.featuredTitle}>Featured this week</h2>
          <div className={classes.highlight}>
            {!featured && <div className={classes.presentation} />}
            {featured && (
              <ButtonElement
                onClick={() => {
                  window.scrollTo({ top: 0 })
                  history.push(`/prompt/${featured.id}`)
                }}
                className={`${classes.presentation} ${
                  !hasAccessToTL ? classes.free : ''
                }`}>
                <span className={classes.presentationHolder}>
                  <Image
                    loading="lazy"
                    src={featured.image_medium_url}
                    alt={featured.title}
                  />
                  <span className={classes.presentationTitle}>
                    <span>{featured.title}</span>
                  </span>
                </span>
              </ButtonElement>
            )}
          </div>
          <div className={classes.presentations}>
            {!presentations &&
              [...new Array(30)].map((_, i) => (
                <div key={i} className={classes.presentation} />
              ))}
            {!!presentations &&
              presentations.map((presentation, i) => {
                return !presentation ? (
                  <div key={i} className={classes.presentation} />
                ) : (
                  <ButtonElement
                    key={i}
                    onClick={() => {
                      window.scrollTo({ top: 0 })
                      history.push(`/prompt/${presentation.id}`)
                    }}
                    className={`${classes.presentation} ${
                      !hasAccessToTL ? 'metrics__restricted--quickwrites' : ''
                    }`}>
                    <span className={classes.presentationHolder}>
                      <Image
                        loading="lazy"
                        src={presentation.image_medium_url}
                        alt={presentation.title}
                      />
                      <span className={classes.presentationTitle}>
                        <span>{presentation.title}</span>
                      </span>
                      {!hasAccessToTL && (
                        <span className={classes.crown}>
                          <FontAwesomeIcon icon={faCrown} />
                        </span>
                      )}
                    </span>
                  </ButtonElement>
                )
              })}
          </div>
        </>
      )
    },
    [classes, featured, hasAccessToTL, presentations, history]
  )

  return (
    <PromptsLayout>
      <div className={classes.container}>
        <main className={classes.main}>
          {memoizedWeeklyJSX}
          <TopicsUpsell
            lessonId={upsell}
            isOpen={upsell}
            requestClose={() => setUpsell(false)}
          />
        </main>
      </div>
    </PromptsLayout>
  )
})
